import { isDefined } from '@sgme/fp';
import type { SgBootstrapTheme, SgBootstrapThemeExtended } from '@sgwt/sgwt-widgets-react';

export const themeKey = 'Theme';

const sgbsVersion = import.meta.env.VITE_SGBS_VERSION;
const cdnUrl = `https://sgwt-cdn-sgbs.sgmarkets.com/sg-bootstrap/${sgbsVersion}`;

/** Initializes SG Bootstrap by loading the correct CSS theme on app startup. */
export function initSgBootstrap() {
  const theme = window.SGWTWidgetConfiguration.context?.getSgDesignSystemTheme();
  const savedTheme = getThemeFromLocalStorage() as SgBootstrapTheme | null;
  const usedTheme = isDefined(savedTheme) ? savedTheme : (theme ?? 'system');

  updateTheme(usedTheme);
}

/** Persists the user's theme preference to localStorage. */
export function saveThemeToLocalStorage(theme: SgBootstrapThemeExtended) {
  if (typeof localStorage === 'undefined') {
    return;
  }
  try {
    localStorage.setItem(themeKey, theme);
  } catch {
    console.error('Unable to store theme, localStorage might be full');
  }
}

/** Returns the resolved theme — always 'dark' or 'standard', never 'system' or undefined. */
export function getTheme(): SgBootstrapTheme {
  return getVariation(getPreferenceTheme());
}

/** Applies the given theme by swapping the SG Bootstrap CSS files in the document head and updating the data-bs-theme attribute on <html>. */
export function updateTheme(theme: SgBootstrapThemeExtended): void {
  const variation = getVariation(theme);
  document.documentElement.setAttribute('data-bs-theme', variation);
  updateThemeUrls(variation);
}

/** Returns the user's stored theme preference ('dark', 'standard', 'system', or undefined if not set). */
export function getPreferenceTheme(): SgBootstrapThemeExtended | undefined {
  const defaultTheme = window.SGWTWidgetConfiguration.context?.getSgDesignSystemTheme() ?? undefined;
  const savedTheme = getThemeFromLocalStorage() as SgBootstrapTheme;

  const usedTheme = isDefined(savedTheme) ? savedTheme : defaultTheme;

  return usedTheme;
}

/** Resolves any theme value (including 'system' and undefined) to the concrete 'dark' or 'standard' variation using the OS preference as fallback. */
export function getVariation(theme: SgBootstrapThemeExtended | undefined): SgBootstrapTheme {
  switch (theme) {
    case 'standard':
    case 'dark':
      return theme;
    default: {
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      return prefersDark ? 'dark' : 'standard';
    }
  }
}

function updateThemeUrls(theme: SgBootstrapTheme): void {
  for (const [id, url] of Object.entries(sgBoostrapUrls(theme))) {
    appendUniqueLinkToDocument(id, url);
  }
}

/** Returns the raw theme string stored in localStorage, or null if absent. */
export function getThemeFromLocalStorage(): string | null {
  if (typeof localStorage === 'undefined') {
    return null;
  }
  return localStorage.getItem(themeKey);
}

function appendUniqueLinkToDocument(id: string, url: string) {
  const linkElement = document.querySelector<HTMLLinkElement>(`link[id="${id}"]`);
  const newLinkElement = document.createElement('link');
  newLinkElement.rel = 'stylesheet';
  newLinkElement.id = id;
  newLinkElement.href = url;
  if (linkElement !== null) {
    newLinkElement.onload = () => {
      document.head.removeChild(linkElement);
    };
  }
  document.head.appendChild(newLinkElement);
}

function sgBoostrapUrls(variation: SgBootstrapTheme) {
  return {
    sgbs: `${cdnUrl}/core/css/sg-bootstrap-${variation}.min.css`,
    icons: `${cdnUrl}/icons/index.min.css`,
    aggridCondensed: `${cdnUrl}/ag-grid-theme/36-${variation}-condensed.min.css`,
  };
}
